<template>
	<div class="position-relative">
		<div class="authentication-wrapper authentication-basic container-p-y p-4 p-sm-0">
			<div class="authentication-inner py-6" style="max-width: 600px;">
				<div class="card p-md-7 p-1">
						<!-- Already submitted -->
					<div class="card-body mt-1 text-center" v-if="alreadySubmitted">
						<i class="ri-checkbox-circle-line" style="font-size: 48px; color: var(--bs-success);"></i>
						<h5 class="mt-4">{{ $t('backgroundCheck.candidate.alreadySubmitted') }}</h5>
						<p class="text-muted">{{ $t('backgroundCheck.candidate.alreadySubmittedDescription') }}</p>
					</div>

					<!-- Success / Veriff redirect -->
					<div class="card-body mt-1 text-center" v-else-if="submittedSuccess">
						<i class="ri-checkbox-circle-line" style="font-size: 48px; color: var(--bs-success);"></i>
						<h5 class="mt-4">{{ $t('backgroundCheck.candidate.submittedSuccess') }}</h5>
						<p class="text-muted" v-if="verificationUrl">
							{{ $t('backgroundCheck.candidate.redirectingToVerification') }}
						</p>
						<p class="text-muted" v-else>
							{{ $t('backgroundCheck.candidate.verificationLater') }}
						</p>
					</div>

					<!-- Stepper Form -->
					<div class="card-body mt-1" v-else>
						<!-- Stepper indicator -->
						<div class="d-flex justify-content-center mb-5" v-if="step > 1">
							<div class="d-flex align-items-center gap-2">
								<span class="badge rounded-pill" :class="step >= 2 ? 'bg-primary' : 'bg-label-secondary'">1</span>
								<small :class="step >= 2 ? 'text-primary fw-medium' : 'text-muted'">{{ $t('backgroundCheck.candidate.steps.info') }}</small>
								<i class="ri-arrow-right-s-line text-muted"></i>
								<span class="badge rounded-pill" :class="step >= 3 ? 'bg-primary' : 'bg-label-secondary'">2</span>
								<small :class="step >= 3 ? 'text-primary fw-medium' : 'text-muted'">{{ $t('backgroundCheck.candidate.steps.documents') }}</small>
								<i class="ri-arrow-right-s-line text-muted"></i>
								<span class="badge rounded-pill" :class="step >= 4 ? 'bg-primary' : 'bg-label-secondary'">3</span>
								<small :class="step >= 4 ? 'text-primary fw-medium' : 'text-muted'">{{ $t('backgroundCheck.candidate.steps.summary') }}</small>
							</div>
						</div>

						<!-- Step 0: Intro -->
						<div v-if="step === 0">
							<div class="text-center mb-5">
								<i class="ri-shield-check-line" style="font-size: 48px; color: var(--bs-primary);"></i>
								<h4 class="mt-3" v-if="companyName">{{ companyName }}</h4>
								<h5 class="mt-2">{{ $t('backgroundCheck.candidate.title') }}</h5>
								<p class="text-muted mt-3" v-if="companyName">
									{{ $t('backgroundCheck.candidate.intro', { company: companyName }) }}
								</p>
							</div>

							<div class="mb-5" style="font-size: 14px;">
								<p class="mb-3"><strong>{{ $t('backgroundCheck.candidate.stepsIntro') }}</strong></p>
								<div class="d-flex align-items-start gap-3 mb-3">
									<span class="badge bg-primary rounded-pill mt-1">1</span>
									<span><strong>{{ $t('backgroundCheck.candidate.step1Title') }}</strong><br><small class="text-muted">{{ $t('backgroundCheck.candidate.step1Description') }}</small></span>
								</div>
								<div class="d-flex align-items-start gap-3 mb-3">
									<span class="badge bg-primary rounded-pill mt-1">2</span>
									<span><strong>{{ $t('backgroundCheck.candidate.step2Title') }}</strong><br><small class="text-muted">{{ $t('backgroundCheck.candidate.step2Description') }}</small></span>
								</div>
								<div class="d-flex align-items-start gap-3 mb-3">
									<span class="badge bg-primary rounded-pill mt-1">3</span>
									<span><strong>{{ $t('backgroundCheck.candidate.step3Title') }}</strong><br><small class="text-muted">{{ $t('backgroundCheck.candidate.step3Description') }}</small></span>
								</div>
							</div>

							<div class="alert alert-light py-2 px-3 mb-5" style="font-size: 13px;">
								<i class="ri-time-line me-1"></i> {{ $t('backgroundCheck.candidate.timeEstimate') }}
							</div>

							<button class="btn btn-primary d-grid w-100" @click="step = 1">
								{{ $t('backgroundCheck.candidate.start') }}
							</button>
						</div>

						<!-- Step 1: Email -->
						<div v-if="step === 1">
							<h5 class="mb-4 text-center">{{ $t('backgroundCheck.candidate.enterEmail') }}</h5>
							<form @submit.prevent="startCheck">
								<div class="form-floating form-floating-outline mb-5">
									<input type="email" class="form-control" id="email" :placeholder="$t('backgroundCheck.candidate.yourEmail')" v-model="email" />
									<label for="email">{{ $t('common.fields.email') }}</label>
									<small v-if="errors.email" class="text-danger">{{ errors.email }}</small>
								</div>
								<div class="d-flex justify-content-between">
									<button type="button" class="btn btn-outline-secondary" @click="step = 0">{{ $t('common.actions.back') }}</button>
									<button type="submit" class="btn btn-primary" :disabled="loading">
										<span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
										{{ $t('common.actions.continue') }}
									</button>
								</div>
							</form>
						</div>

						<!-- Step 2: Personal Info -->
						<div v-if="step === 2">
							<h5 class="mb-4">{{ $t('backgroundCheck.candidate.personalInfo') }}</h5>
							<form @submit.prevent="goToStep(3)">
								<div class="row g-4">
									<div class="col-md-6">
										<div class="form-floating form-floating-outline">
											<input type="text" class="form-control" id="firstName" :placeholder="$t('common.fields.firstName')" v-model="form.firstName" @blur="saveInfo" />
											<label for="firstName">{{ $t('common.fields.firstName') }}</label>
										</div>
									</div>
									<div class="col-md-6">
										<div class="form-floating form-floating-outline">
											<input type="text" class="form-control" id="lastName" :placeholder="$t('common.fields.lastName')" v-model="form.lastName" @blur="saveInfo" />
											<label for="lastName">{{ $t('common.fields.lastName') }}</label>
										</div>
									</div>
									<div class="col-md-6">
										<div class="form-floating form-floating-outline">
											<input type="tel" class="form-control" id="phoneNumber" placeholder="+33612345678" v-model="form.phoneNumber" @blur="saveInfo" />
											<label for="phoneNumber">{{ $t('common.fields.phone') }}</label>
										</div>
									</div>
									<div class="col-md-6">
										<div class="form-floating form-floating-outline">
											<input type="date" class="form-control" id="birthDate" v-model="form.birthDate" @blur="saveInfo" />
											<label for="birthDate">{{ $t('common.fields.birthDate') }}</label>
										</div>
									</div>
								</div>
								<div class="d-flex justify-content-between mt-5">
									<button type="button" class="btn btn-outline-secondary" @click="step = 1">{{ $t('common.actions.back') }}</button>
									<button type="submit" class="btn btn-primary">{{ $t('common.actions.continue') }}</button>
								</div>
							</form>
						</div>

						<!-- Step 3: Document Upload -->
						<div v-if="step === 3">
							<h5 class="mb-4">{{ $t('backgroundCheck.candidate.requiredDocuments') }}</h5>
							<p class="text-muted mb-4">{{ $t('backgroundCheck.candidate.documentsDescription') }}</p>

							<!-- CV -->
							<div class="card mb-3" :class="dossier.hasCV ? 'border-success' : ''">
								<div class="card-body py-3">
									<div class="d-flex align-items-center justify-content-between">
										<div class="d-flex align-items-center gap-3">
											<i class="ri-file-text-line" style="font-size: 24px;" :class="dossier.hasCV ? 'text-success' : 'text-muted'"></i>
											<div>
												<strong>{{ $t('backgroundCheck.candidate.cv') }}</strong>
												<br><small class="text-muted">{{ $t('backgroundCheck.candidate.cvDescription') }}</small>
											</div>
										</div>
										<div class="d-flex align-items-center gap-2">
											<i v-if="dossier.hasCV" class="ri-checkbox-circle-fill text-success" style="font-size: 20px;"></i>
											<label class="btn btn-sm btn-outline-primary mb-0">
												<i class="ri-upload-2-line me-1"></i> {{ dossier.hasCV ? $t('common.actions.replace') : $t('common.actions.choose') }}
												<input type="file" accept=".pdf" class="d-none" @change="uploadFile($event, 'cv')" />
											</label>
										</div>
									</div>
								</div>
							</div>

							<!-- Cover Letter -->
							<div class="card mb-3" :class="dossier.hasCoverLetter ? 'border-success' : ''">
								<div class="card-body py-3">
									<div class="d-flex align-items-center justify-content-between">
										<div class="d-flex align-items-center gap-3">
											<i class="ri-file-text-line" style="font-size: 24px;" :class="dossier.hasCoverLetter ? 'text-success' : 'text-muted'"></i>
											<div>
												<strong>{{ $t('backgroundCheck.candidate.coverLetter') }}</strong>
												<br><small class="text-muted">{{ $t('backgroundCheck.candidate.coverLetterDescription') }}</small>
											</div>
										</div>
										<div class="d-flex align-items-center gap-2">
											<i v-if="dossier.hasCoverLetter" class="ri-checkbox-circle-fill text-success" style="font-size: 20px;"></i>
											<label class="btn btn-sm btn-outline-primary mb-0">
												<i class="ri-upload-2-line me-1"></i> {{ dossier.hasCoverLetter ? $t('common.actions.replace') : $t('common.actions.choose') }}
												<input type="file" accept=".pdf" class="d-none" @change="uploadFile($event, 'cover-letter')" />
											</label>
										</div>
									</div>
								</div>
							</div>

							<!-- B3 Extract -->
							<div class="card mb-3" :class="dossier.hasB3Extract ? 'border-success' : ''">
								<div class="card-body py-3">
									<div class="d-flex align-items-center justify-content-between">
										<div class="d-flex align-items-center gap-3">
											<i class="ri-shield-check-line" style="font-size: 24px;" :class="dossier.hasB3Extract ? 'text-success' : 'text-muted'"></i>
											<div>
												<strong>{{ $t('backgroundCheck.candidate.b3Extract') }}</strong>
											</div>
										</div>
										<div class="d-flex align-items-center gap-2">
											<i v-if="dossier.hasB3Extract" class="ri-checkbox-circle-fill text-success" style="font-size: 20px;"></i>
											<label class="btn btn-sm btn-outline-primary mb-0">
												<i class="ri-upload-2-line me-1"></i> {{ dossier.hasB3Extract ? $t('common.actions.replace') : $t('common.actions.choose') }}
												<input type="file" accept=".pdf" class="d-none" @change="uploadFile($event, 'b3-extract')" />
											</label>
										</div>
									</div>
									<div class="alert alert-light mt-3 mb-0 py-2 px-3" style="font-size: 13px;">
										<strong>{{ $t('backgroundCheck.candidate.howToGetB3') }}</strong>
										<ol class="mb-0 mt-1 ps-3">
											<li>{{ $t('backgroundCheck.candidate.b3Step1') }} <a href="https://casier-judiciaire.justice.gouv.fr" target="_blank" rel="noopener">casier-judiciaire.justice.gouv.fr</a></li>
											<li>{{ $t('backgroundCheck.candidate.b3Step2') }}</li>
											<li>{{ $t('backgroundCheck.candidate.b3Step3') }}</li>
										</ol>
										<small class="text-muted d-block mt-1">{{ $t('backgroundCheck.candidate.b3Note') }}</small>
									</div>
								</div>
							</div>

							<div class="d-flex justify-content-between mt-5">
								<button type="button" class="btn btn-outline-secondary" @click="step = 2">{{ $t('common.actions.back') }}</button>
								<button type="button" class="btn btn-primary" @click="goToStep(4)" :disabled="!allDocumentsUploaded">
									{{ allDocumentsUploaded ? $t('common.actions.continue') : $t('backgroundCheck.candidate.uploadAllDocuments') }}
								</button>
							</div>
						</div>

						<!-- Step 4: Summary -->
						<div v-if="step === 4">
							<h5 class="mb-4">{{ $t('backgroundCheck.candidate.steps.summary') }}</h5>

							<div class="card mb-3">
								<div class="card-body">
									<div class="row g-3">
										<div class="col-6">
											<small class="text-muted d-block">{{ $t('common.fields.firstName') }}</small>
											<strong>{{ form.firstName || '—' }}</strong>
										</div>
										<div class="col-6">
											<small class="text-muted d-block">{{ $t('common.fields.lastName') }}</small>
											<strong>{{ form.lastName || '—' }}</strong>
										</div>
										<div class="col-6">
											<small class="text-muted d-block">{{ $t('common.fields.email') }}</small>
											<strong>{{ email }}</strong>
										</div>
										<div class="col-6">
											<small class="text-muted d-block">{{ $t('common.fields.phone') }}</small>
											<strong>{{ form.phoneNumber || '—' }}</strong>
										</div>
										<div class="col-6">
											<small class="text-muted d-block">{{ $t('common.fields.birthDate') }}</small>
											<strong>{{ form.birthDate || '—' }}</strong>
										</div>
									</div>
								</div>
							</div>

							<div class="card mb-3">
								<div class="card-body">
									<div class="d-flex align-items-center gap-2 mb-2">
										<i :class="dossier.hasCV ? 'ri-checkbox-circle-fill text-success' : 'ri-close-circle-fill text-danger'" style="font-size: 18px;"></i>
										<span>{{ $t('backgroundCheck.candidate.cv') }}</span>
									</div>
									<div class="d-flex align-items-center gap-2 mb-2">
										<i :class="dossier.hasCoverLetter ? 'ri-checkbox-circle-fill text-success' : 'ri-close-circle-fill text-danger'" style="font-size: 18px;"></i>
										<span>{{ $t('backgroundCheck.candidate.coverLetter') }}</span>
									</div>
									<div class="d-flex align-items-center gap-2">
										<i :class="dossier.hasB3Extract ? 'ri-checkbox-circle-fill text-success' : 'ri-close-circle-fill text-danger'" style="font-size: 18px;"></i>
										<span>{{ $t('backgroundCheck.candidate.b3ExtractShort') }}</span>
									</div>
								</div>
							</div>

							<div class="form-check mt-4">
								<input class="form-check-input" type="checkbox" id="consent" v-model="consentGiven">
								<label class="form-check-label" for="consent" style="font-size: 13px;">
									{{ $t('backgroundCheck.candidate.dataConsent') }}
								</label>
							</div>

							<div v-if="submitError" class="alert alert-danger mb-4 mt-3">{{ submitError }}</div>

							<div class="d-flex justify-content-between mt-4">
								<button type="button" class="btn btn-outline-secondary" @click="step = 3">{{ $t('common.actions.back') }}</button>
								<button type="button" class="btn btn-primary" @click="submitDossier" :disabled="loading || !consentGiven">
									<span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
									{{ $t('backgroundCheck.candidate.submit') }}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import "@/assets/vendor/css/pages/page-auth.css";
import axios from "axios";

const API_URL = process.env.VUE_APP_API_URL;

export default {
	name: "BackgroundCheckCandidateForm",

	data() {
		return {
			step: 0,
			loading: false,
			companyToken: null,
			candidateToken: null,
			companyName: null,
			email: "",
			alreadySubmitted: false,
			submittedSuccess: false,
			verificationUrl: null,
			submitError: null,
			form: {
				firstName: null,
				lastName: null,
				phoneNumber: null,
				birthDate: null,
			},
			dossier: {
				hasCV: false,
				hasCoverLetter: false,
				hasB3Extract: false,
			},
			errors: {},
			consentGiven: false,
		};
	},

	async mounted() {
		this.companyToken = this.$route.params.companyToken;

		// Fetch company name from public token
		if (this.companyToken) {
			try {
				const res = await axios.get(`${API_URL}/background-check/${this.companyToken}/info`);
				this.companyName = res.data.companyName;
			} catch (e) {
				// Ignore — companyName will stay null
			}
		}

		// Resume from magic link route (/background-check/resume/:token)
		if (this.$route.params.token) {
			this.candidateToken = this.$route.params.token;
			localStorage.setItem("bgCheckToken", this.$route.params.token);
			await this.resumeDossier();
			return;
		}

		// Resume from query param (/background-check/:companyToken?token=xxx)
		const token = this.$route.query.token;
		if (token) {
			this.candidateToken = token;
			localStorage.setItem("bgCheckToken", token);
			window.history.replaceState({}, "", this.$route.path);
			await this.resumeDossier();
			return;
		}

		// Check localStorage for existing session
		const savedToken = localStorage.getItem("bgCheckToken");
		if (savedToken) {
			this.candidateToken = savedToken;
			await this.resumeDossier();
		}
	},

	computed: {
		allDocumentsUploaded() {
			return this.dossier.hasCV && this.dossier.hasCoverLetter && this.dossier.hasB3Extract;
		},
	},

	methods: {
		async startCheck() {
			this.errors = {};
			const trimmed = (this.email || "").trim();
			this.email = trimmed;

			if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
				this.errors.email = this.$t('backgroundCheck.candidate.invalidEmail');
				return;
			}

			this.loading = true;
			try {
				const response = await axios.post(
					`${API_URL}/background-check/${this.companyToken}/start`,
					{ email: this.email }
				);
				this.candidateToken = response.data.token;
				localStorage.setItem("bgCheckToken", response.data.token);
				await this.resumeDossier();
			} catch (error) {
				const msg = error?.response?.data?.message || this.$t('errors.generic');
				alert(msg);
			} finally {
				this.loading = false;
			}
		},

		async resumeDossier() {
			try {
				const response = await axios.get(
					`${API_URL}/background-check/resume/${this.candidateToken}`
				);
				const data = response.data;

				if (data.status !== "draft") {
					this.alreadySubmitted = true;
					return;
				}

				this.companyName = data.companyName;
				this.email = data.email;
				this.form.firstName = data.firstName;
				this.form.lastName = data.lastName;
				this.form.phoneNumber = data.phoneNumber;
				this.form.birthDate = data.birthDate;
				this.dossier.hasCV = data.hasCV;
				this.dossier.hasCoverLetter = data.hasCoverLetter;
				this.dossier.hasB3Extract = data.hasB3Extract;

				// Go to step 2 (info form)
				if (this.step <= 1) {
					this.step = 2;
				}
			} catch (error) {
				// Token invalid — clear it
				localStorage.removeItem("bgCheckToken");
				this.candidateToken = null;
				this.step = 0;
				const msg = error?.response?.data?.message || this.$t('backgroundCheck.candidate.invalidLink');
				alert(msg);
			}
		},

		async saveInfo() {
			if (!this.candidateToken) return;

			try {
				await axios.put(
					`${API_URL}/background-check/resume/${this.candidateToken}`,
					{
						firstName: this.form.firstName,
						lastName: this.form.lastName,
						phoneNumber: this.form.phoneNumber,
						birthDate: this.form.birthDate,
					}
				);
			} catch (error) {
				console.error("Save error:", error);
			}
		},

		async uploadFile(event, type) {
			const file = event.target.files[0];
			if (!file) return;

			if (file.type !== "application/pdf") {
				alert(this.$t('backgroundCheck.candidate.pdfOnly'));
				return;
			}

			if (file.size > 10 * 1024 * 1024) {
				alert(this.$t('backgroundCheck.candidate.fileTooLarge'));
				return;
			}

			const formData = new FormData();
			formData.append("file", file);

			try {
				await axios.post(
					`${API_URL}/background-check/resume/${this.candidateToken}/upload/${type}`,
					formData,
					{ headers: { "Content-Type": "multipart/form-data" } }
				);

				// Update flags
				if (type === "cv") this.dossier.hasCV = true;
				if (type === "cover-letter") this.dossier.hasCoverLetter = true;
				if (type === "b3-extract") this.dossier.hasB3Extract = true;
			} catch (error) {
				const msg = error?.response?.data?.message || this.$t('backgroundCheck.candidate.uploadError');
				alert(msg);
			}

			// Reset input
			event.target.value = "";
		},

		goToStep(targetStep) {
			this.step = targetStep;
		},

		async submitDossier() {
			this.submitError = null;
			this.loading = true;

			try {
				const response = await axios.post(
					`${API_URL}/background-check/resume/${this.candidateToken}/submit`
				);

				this.submittedSuccess = true;
				localStorage.removeItem("bgCheckToken");

				if (response.data.verificationUrl) {
					this.verificationUrl = response.data.verificationUrl;
					setTimeout(() => {
						window.location.href = response.data.verificationUrl;
					}, 2000);
				}
			} catch (error) {
				const msg = error?.response?.data?.message || this.$t('backgroundCheck.candidate.submitError');
				this.submitError = msg;
			} finally {
				this.loading = false;
			}
		},
	},
};
</script>
